import React from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { FlagDefinition } from '@/API';

interface MLInactiveFlag {
  key: string;
  docCount: number;
  originalCount?: number;
  isDefaultEnabled: boolean;
}

interface FlagsListProps {
  flags: MLInactiveFlag[];
  flagDefinitions?: FlagDefinition[];
  onToggle: (flagKey: string) => void;
  disabled: boolean;
  updating: boolean;
}

const FlagsList: React.FC<FlagsListProps> = ({
  flags,
  flagDefinitions = [],
  onToggle,
  disabled,
  updating,
}) => {
  const getFlagDisplayName = (flagKey: string) => {
    // Look for a matching flag definition
    const flagDefinition = flagDefinitions.find(
      def => def.flagValue === flagKey
    );

    if (flagDefinition?.friendlyName) {
      return flagDefinition.friendlyName;
    }

    // Fallback to original format (replace underscores with spaces but keep the original structure)
    return flagKey.replace(/_/g, ' ');
  };

  const getFlagDescription = (flagKey: string) => {
    // Look for a matching flag definition
    const flagDefinition = flagDefinitions.find(
      def => def.flagValue === flagKey
    );

    return flagDefinition?.description || null;
  };

  const renderFlagSecondary = (flag: MLInactiveFlag) => {
    const description = getFlagDescription(flag.key);

    return (
      <Box
        component="span"
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
      >
        {/* Description (if available) */}
        {description && (
          <Typography variant="body2" color="text.secondary" component="span">
            {description}
          </Typography>
        )}

        {/* Record count information */}
        <Box
          component="span"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          {flag.isDefaultEnabled ? (
            <Typography
              variant="body2"
              color="error.main"
              component="span"
              sx={{ fontWeight: 'medium' }}
            >
              ✓ Will exclude {(flag.originalCount || 0).toLocaleString()}{' '}
              records
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" component="span">
              Would exclude: {flag.docCount.toLocaleString()} records
            </Typography>
          )}
          {updating && <CircularProgress size={12} />}
        </Box>
      </Box>
    );
  };

  return (
    <List sx={{ width: '100%' }}>
      {flags.map((flag, index, array) => (
        <React.Fragment key={flag.key}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => onToggle(flag.key)}
              disabled={disabled}
              sx={{
                '&:hover': { bgcolor: 'action.hover' },
                ...(flag.isDefaultEnabled && { bgcolor: 'primary.50' }),
              }}
            >
              <ListItemIcon>
                <Checkbox
                  checked={flag.isDefaultEnabled}
                  disabled={disabled}
                  edge="start"
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body1" component="span">
                    {getFlagDisplayName(flag.key)}
                  </Typography>
                }
                secondary={renderFlagSecondary(flag)}
              />
            </ListItemButton>
          </ListItem>
          {index < array.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>
  );
};

export default FlagsList;
